import { ApiError } from "@anna-money/anna-web-lib/api";
import { makeAutoObservable, runInAction } from "mobx";
import { type IAnalyticsManager } from "services/analytics/analyticsManager";
import { type BillingClient } from "services/billing/billingClient";
import { PaymentStatus } from "services/billing/billingTypes";

// Drives the refund button on the compliance-rejection screen: reads whether a refund is available
// on mount, then requests one once the customer confirms.
export class RefundStore {
  isLoaded = false;
  // A failed read (network, server, deserialization) is kept distinct from a genuine "no refund
  // available" — otherwise an eligible rejected customer would see neither a button nor an error,
  // just silence, until they happened to reload and the request happened to succeed.
  loadError = false;
  canRequestRefund = false;
  amount: string | undefined = undefined;
  isRequesting = false;
  isRefunded = false;
  isRefundRequested = false;
  errorMessage: string | undefined = undefined;

  constructor(
    private readonly _billingClient: BillingClient,
    private readonly _analyticsManager: IAnalyticsManager,
  ) {
    makeAutoObservable(this);
  }

  async init(): Promise<void> {
    runInAction(() => {
      this.loadError = false;
    });
    let info;
    try {
      info = await this._billingClient.getPaymentStatus();
    } catch {
      runInAction(() => {
        this.loadError = true;
        this.isLoaded = true;
      });
      this._analyticsManager.event("compliance-rejection-payment-info-failed");
      return;
    }

    runInAction(() => {
      this.canRequestRefund = info.canRequestRefund;
      this.amount = info.amount;
      // Already refunded (or requested) before this visit (an earlier session, or support) — show
      // that instead of silently offering nothing, since canRequestRefund is false either way.
      this.isRefunded = info.status === PaymentStatus.Refunded;
      this.isRefundRequested = info.status === PaymentStatus.RefundRequested;
      this.isLoaded = true;
    });
  }

  async requestRefund(): Promise<void> {
    if (this.isRequesting) {
      return;
    }

    runInAction(() => {
      this.isRequesting = true;
      this.errorMessage = undefined;
    });
    this._analyticsManager.event("compliance-rejection-refund-requested");

    try {
      const result = await this._billingClient.refund();
      runInAction(() => {
        // The backend responds with REFUND_REQUESTED immediately - actual completion (REFUNDED)
        // happens asynchronously later, once anna-fees confirms it. Read the real status back
        // rather than assuming, since a request can occasionally land already-completed too.
        this.isRefunded = result.status === PaymentStatus.Refunded;
        this.isRefundRequested = result.status === PaymentStatus.RefundRequested;
        this.canRequestRefund = false;
      });
      this._analyticsManager.event("compliance-rejection-refund-succeeded");
    } catch (error) {
      runInAction(() => {
        // The backend explains why in plain English (already refunded, already sent to Companies
        // House, etc.) — worth showing verbatim rather than a generic message. A non-API failure
        // (network, timeout) still needs a visible fallback, though, so the customer isn't left
        // thinking a confirmed refund silently vanished.
        this.errorMessage =
          error instanceof ApiError ? error.message : "Something went wrong while requesting your refund";
      });
      this._analyticsManager.event("compliance-rejection-refund-failed");
    } finally {
      runInAction(() => {
        this.isRequesting = false;
      });
    }
  }
}
